import { create } from 'zustand';
import type { ShortcutId } from '../onboarding/shortcutCatalog';
import { SHORTCUTS } from '../onboarding/shortcutCatalog';
import {
  evaluateNudge,
  getSessionStartedAt,
  NUDGE_VISIBLE_MS,
} from '../onboarding/shortcutCoach';
import { TUTORIAL_STEPS } from '../onboarding/tutorialSteps';

const STORAGE_KEY = 'velostruct-onboarding';
const SCHEMA_VERSION = 1;

export interface ShortcutNudge {
  actionId: ShortcutId;
  shownAt: number;
}

type TutorialStatus = 'pending' | 'active' | 'completed' | 'skipped';

interface PersistedOnboarding {
  v: number;
  tutorialStatus: TutorialStatus;
  tutorialStep: number;
  nudgesEnabled: boolean;
  keyboardUses: Record<string, number>;
  nudgeShows: Record<string, number>;
  dismissedActions: string[];
  lastNudgeAtByAction: Record<string, number>;
}

function loadPersisted(): Partial<PersistedOnboarding> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Existing installs already have settings — don't ambush them with the tour.
      if (localStorage.getItem('velostruct-settings')) {
        return { tutorialStatus: 'completed' };
      }
      return {};
    }
    const parsed = JSON.parse(raw) as Partial<PersistedOnboarding>;
    if (parsed.v !== SCHEMA_VERSION) return {};
    return parsed;
  } catch {
    return {};
  }
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;

function persist(state: OnboardingStore) {
  const payload: PersistedOnboarding = {
    v: SCHEMA_VERSION,
    tutorialStatus: state.tutorialStatus,
    tutorialStep: state.tutorialStep,
    nudgesEnabled: state.nudgesEnabled,
    keyboardUses: state.keyboardUses,
    nudgeShows: state.nudgeShows,
    dismissedActions: state.dismissedActions,
    lastNudgeAtByAction: state.lastNudgeAtByAction,
  };
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota */
    }
  }, 80);
}

let nudgeHideTimer: ReturnType<typeof setTimeout> | null = null;

interface OnboardingStore {
  tutorialStatus: TutorialStatus;
  tutorialStep: number;
  nudgesEnabled: boolean;
  keyboardUses: Record<string, number>;
  nudgeShows: Record<string, number>;
  dismissedActions: string[];
  lastNudgeAt: number;
  lastNudgeAtByAction: Record<string, number>;
  currentNudge: ShortcutNudge | null;

  reportPointer: (id: ShortcutId) => void;
  reportKeyboard: (id: ShortcutId) => void;
  dismissNudge: () => void;
  muteAction: (id: ShortcutId) => void;
  setNudgesEnabled: (enabled: boolean) => void;

  startTutorial: () => void;
  nextTutorialStep: () => void;
  prevTutorialStep: () => void;
  skipTutorial: () => void;
  completeTutorial: () => void;
}

const persisted = loadPersisted();

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  tutorialStatus: persisted.tutorialStatus ?? 'pending',
  tutorialStep: persisted.tutorialStep ?? 0,
  nudgesEnabled: persisted.nudgesEnabled ?? true,
  keyboardUses: persisted.keyboardUses ?? {},
  nudgeShows: persisted.nudgeShows ?? {},
  dismissedActions: persisted.dismissedActions ?? [],
  lastNudgeAt: 0,
  lastNudgeAtByAction: persisted.lastNudgeAtByAction ?? {},
  currentNudge: null,

  reportPointer: (id) => {
    if (!SHORTCUTS[id]) return;
    const state = get();
    const now = Date.now();
    const decision = evaluateNudge({
      nudgesEnabled: state.nudgesEnabled,
      tutorialActive: state.tutorialStatus === 'active',
      dismissedActions: state.dismissedActions,
      keyboardUses: state.keyboardUses,
      nudgeShows: state.nudgeShows,
      lastNudgeAt: state.lastNudgeAt,
      lastNudgeAtByAction: state.lastNudgeAtByAction,
      hasVisibleNudge: state.currentNudge !== null,
      sessionStartedAt: getSessionStartedAt(),
      now,
    }, id);

    if (decision !== 'show') return;

    if (nudgeHideTimer) clearTimeout(nudgeHideTimer);
    set(current => {
      const next: Partial<OnboardingStore> = {
        currentNudge: { actionId: id, shownAt: now },
        lastNudgeAt: now,
        lastNudgeAtByAction: { ...current.lastNudgeAtByAction, [id]: now },
        nudgeShows: { ...current.nudgeShows, [id]: (current.nudgeShows[id] ?? 0) + 1 },
      };
      return next;
    });
    persist(get());
    nudgeHideTimer = setTimeout(() => {
      const still = get().currentNudge;
      if (still && still.actionId === id && still.shownAt === now) {
        set({ currentNudge: null });
      }
    }, NUDGE_VISIBLE_MS);
  },

  reportKeyboard: (id) => {
    if (!SHORTCUTS[id]) return;
    set(state => {
      const keyboardUses = { ...state.keyboardUses, [id]: (state.keyboardUses[id] ?? 0) + 1 };
      const hideSame = state.currentNudge?.actionId === id;
      return {
        keyboardUses,
        currentNudge: hideSame ? null : state.currentNudge,
      };
    });
    persist(get());
  },

  dismissNudge: () => {
    if (nudgeHideTimer) clearTimeout(nudgeHideTimer);
    set({ currentNudge: null });
  },

  muteAction: (id) => {
    if (nudgeHideTimer) clearTimeout(nudgeHideTimer);
    set(state => ({
      dismissedActions: state.dismissedActions.includes(id)
        ? state.dismissedActions
        : [...state.dismissedActions, id],
      currentNudge: state.currentNudge?.actionId === id ? null : state.currentNudge,
    }));
    persist(get());
  },

  setNudgesEnabled: (enabled) => {
    if (!enabled && nudgeHideTimer) clearTimeout(nudgeHideTimer);
    set({
      nudgesEnabled: enabled,
      currentNudge: enabled ? get().currentNudge : null,
    });
    persist(get());
  },

  startTutorial: () => {
    if (nudgeHideTimer) clearTimeout(nudgeHideTimer);
    set({
      tutorialStatus: 'active',
      tutorialStep: 0,
      currentNudge: null,
    });
    persist(get());
  },

  nextTutorialStep: () => {
    const { tutorialStep } = get();
    if (tutorialStep >= TUTORIAL_STEPS.length - 1) {
      get().completeTutorial();
      return;
    }
    set({ tutorialStep: tutorialStep + 1 });
    persist(get());
  },

  prevTutorialStep: () => {
    const { tutorialStep } = get();
    if (tutorialStep <= 0) return;
    set({ tutorialStep: tutorialStep - 1 });
    persist(get());
  },

  skipTutorial: () => {
    set({ tutorialStatus: 'skipped', tutorialStep: 0, currentNudge: null });
    persist(get());
  },

  completeTutorial: () => {
    set({ tutorialStatus: 'completed', tutorialStep: 0, currentNudge: null });
    persist(get());
  },
}));
